// Stabile Block-IDs (Audit M7, 2026-09-24).
//
// Bis hierhin fand TTSView einen Block nach dem `await generateTts()` per
// Objektidentität (`liveBlocks.indexOf(block)`) wieder. Jede Stelle, die
// Blöcke neu anlegt — Stern-Klick (autoPositionFavOnTimeline klonte ALLE
// Blöcke), Tippen im generierenden Block, Reset Positions — ließ den Index
// auf -1 laufen, und der fertige, bezahlte Take wurde still verworfen.
//
// Jeder Block trägt deshalb eine `id`, die über Spreads, Reorder und Klone
// hinweg gleich bleibt. Alt-Bestand in IndexedDB (Sessions vor diesem Fix)
// hat keine IDs — `ensureBlockIds` vergibt sie beim Restore nach.

import type { Block, SessionState } from '@shared/types';

let counter = 0;

/** Neue, sessionweit eindeutige Block-ID. Zeitstempel + Zähler, damit zwei
 *  Blöcke aus derselben Millisekunde (Auto-Split) verschieden bleiben. */
export function newBlockId(): string {
  counter += 1;
  return `blk-${Date.now().toString(36)}-${counter.toString(36)}`;
}

/** Vergibt fehlende oder doppelte IDs nach. Blöcke, die schon eine
 *  eindeutige ID haben, werden per Referenz zurückgegeben (kein Klon). */
export function ensureBlockIds(blocks: Block[]): Block[] {
  const seen = new Set<string>();
  let changed = false;
  const next = blocks.map((b) => {
    const id = (b as Partial<Block>).id;
    if (typeof id === 'string' && id.length > 0 && !seen.has(id)) {
      seen.add(id);
      return b;
    }
    changed = true;
    const fresh = newBlockId();
    seen.add(fresh);
    return { ...b, id: fresh };
  });
  return changed ? next : blocks;
}

function ensureMap(m: Record<number, Block[]>): Record<number, Block[]> {
  const out: Record<number, Block[]> = {};
  for (const [k, v] of Object.entries(m ?? {})) out[Number(k)] = ensureBlockIds(v);
  return out;
}

/** Restore-Migration: alle Block-Listen einer Session mit IDs versehen
 *  (`blocks`, `assetBlocks`, `freeBlocks`). */
export function ensureSessionBlockIds(s: SessionState): SessionState {
  const freeBlocks: SessionState['freeBlocks'] = {};
  for (const [k, modes] of Object.entries(s.freeBlocks ?? {})) {
    freeBlocks[Number(k)] = {
      tts: ensureBlockIds(modes?.tts ?? []),
      sts: ensureBlockIds(modes?.sts ?? []),
    };
  }
  return {
    ...s,
    blocks: ensureBlockIds(s.blocks ?? []),
    assetBlocks: ensureMap(s.assetBlocks),
    freeBlocks,
  };
}

/** Index des Blocks mit `id`, -1 wenn er nicht (mehr) existiert. */
export function findBlockIndexById(blocks: Block[], id: string): number {
  return blocks.findIndex((b) => b.id === id);
}
